import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { cableConsumer, registerCableIdleChecker, subscribeCableReconnect } from "@/lib/cable";
import { readRememberedAccountId, useOptionalAccountWorkspace } from "@/lib/account-workspace";
import {
  applyHermesStreamDelta,
  createHermesApi,
  foldHermesStreamCatchUp,
  hermesInFlightTurn,
  HERMES_AUTO_REOPEN_ON_DONE,
  isBrandSiteDraftMutatingToolName,
  isHermesTerminalEvent,
  hermesStreamEventsUnlockSend,
  mergeHermesMessages,
  parseKbBindingsFromContext,
  parseHermesCablePayload,
  removeHermesMessage,
  removeHermesTurn,
  statusFromStreamEvent,
  type HermesChat,
  type HermesMessage,
  type HermesSkillId,
  type KbWikiBinding,
} from "@/lib/hermes-api";
import {
  knowledgeEntryChatContext,
  type KnowledgeEntryContext,
} from "@/lib/hermes-knowledge-entry";
import {
  clearJourneyAuthoringModeContext,
  isJourneyAuthoringChat,
  journeyAuthoringChatContext,
  journeyAuthoringFromChat,
  matchesJourneyAuthoringChat,
  type JourneyAuthoringContext,
} from "@/lib/hermes-journey-authoring";
import {
  brandSiteAuthoringChatContext,
  brandSiteAuthoringFromChat,
  clearBrandSiteAuthoringModeContext,
  isBrandSiteAuthoringChat,
  type BrandSiteAuthoringContext,
} from "@/lib/hermes-brand-site-authoring";
import {
  brandSiteStudioSkills,
  buildBrandSiteStudioChatContext,
  buildBrandSiteStudioLockedDraft,
  mergeBrandSiteStudioFloor,
} from "@/lib/hermes-brand-site-studio";
import {
  brandSiteScoreOptimizeChatContext,
  brandSiteScoreOptimizeFromChat,
  isBrandSiteScoreOptimizeChat,
  shouldAcceptBrandSiteScoreOptimizeStart,
  shouldReplaceBrandSiteScoreOptimize,
  type BrandSiteScoreOptimizeContext,
} from "@/lib/hermes-brand-site-score-optimize";
import {
  isPlatformTemplateIdeChat,
  platformTemplateIdeChatContext,
  platformTemplateIdeFromChat,
  type PlatformTemplateIdeContext,
} from "@/lib/hermes-platform-template-ide";
import { readHermesLastChatId, writeHermesLastChatId } from "@/lib/hermes-last-chat";
import {
  enqueueHermesMessage,
  removeHermesQueuedMessage,
  updateHermesQueuedMessage,
  type HermesQueuedMessage,
} from "@/lib/hermes-send-queue";
import {
  applyKnowledgeEntryOverride,
  buildDefaultCapabilityDraft,
  buildDefaultCapabilityDraftFromWikis,
  capabilityContextFields,
  capabilityDraftFromChat,
  persistSkillsForDraft,
  shouldApplySeededCapabilityDraft,
  unseededCapabilityDraft,
  withBrandSiteAuthoringMode,
  withJourneyAuthoringMode,
  type BrandToolBinding,
  type CapabilityDraft,
  type MaterialsBinding,
} from "@/lib/hermes-capability-bindings";
import { brandIdFromPath } from "@/lib/kb-routes";
import { createKbApi } from "@/lib/kb-api";
import type { BrandSiteDraftMutationHint } from "@/lib/brand-site-draft-apply";

type HermesCableSubscription = {
  perform: (action: string, data?: Record<string, unknown>) => void;
  unsubscribe: () => void;
};

function skillsWithKbBindings(
  skills: HermesSkillId[],
  bindings: KbWikiBinding[],
): HermesSkillId[] {
  const withoutKb = skills.filter((skill) => skill !== "kb");
  return bindings.length > 0 ? (["kb", ...withoutKb] as HermesSkillId[]) : withoutKb;
}

/** 路径里的品牌 id 转正整数；无品牌上下文则为 null。 */
function brandIdNumberFromPath(pathname: string): number | null {
  const raw = brandIdFromPath(pathname);
  if (raw == null) return null;
  const brandId = Number(raw);
  return Number.isInteger(brandId) && brandId > 0 ? brandId : null;
}

/** UI 技能芯片只要 kb/fetch；no_fetch 只用于持久化。 */
function hermesUiSkills(skills: string[]): HermesSkillId[] {
  return skills.filter((skill): skill is HermesSkillId => skill === "kb" || skill === "fetch");
}


type HermesContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Session-only: Hermes column nearly fills main+hermes row. */
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  accountId: string;
  chatId: number | null;
  chatSkills: HermesSkillId[];
  draftSkills: HermesSkillId[];
  setDraftSkills: (skills: HermesSkillId[]) => void;
  draftKbBindings: KbWikiBinding[];
  setDraftKbBindings: (bindings: KbWikiBinding[]) => void;
  capabilityDraft: CapabilityDraft;
  setCapabilityDraft: (draft: CapabilityDraft, options?: { persist?: boolean }) => void;
  messages: HermesMessage[];
  isSending: boolean;
  streamStatus: string | null;
  /** 生成中入队；空闲时直接发送 */
  sendOrEnqueue: (content: string) => Promise<{ queued: boolean }>;
  sendMessage: (content: string) => Promise<void>;
  /** 待发送队列（不进 messages） */
  queuedMessages: HermesQueuedMessage[];
  enqueueMessage: (content: string) => void;
  updateQueuedMessage: (id: string, content: string) => void;
  removeQueuedMessage: (id: string) => HermesQueuedMessage | null;
  /** 打断当前生成并立刻发送该队列项 */
  sendQueuedNow: (id: string) => Promise<void>;
  /** Re-generate after a failed/cancelled assistant reply (no duplicate user bubble). */
  retryMessage: (assistantMessageId: number) => Promise<void>;
  /** Cancel in-flight generation and drop the turn; returns the last user prompt to restore into the composer. */
  stopGeneration: () => string | null;
  startNewSession: () => void;
  switchSession: (chatId: number) => Promise<void>;
  /** 新建会话后递增；抽屉据此聚焦右侧输入框（Popover / 侧栏共用） */
  composerFocusNonce: number;
  /** 正在拉取目标会话消息（右侧主面板展示加载态，左侧列表保持不动） */
  isSwitchingSession: boolean;
  knowledgeEntry: KnowledgeEntryContext | null;
  startKnowledgeEntrySession: (ctx: KnowledgeEntryContext) => void;
  updateKnowledgeEntryStructured: (structured: Record<string, unknown>) => void;
  /** 旅程手册创作会话（EditPage「AI 协助」） */
  journeyAuthoring: JourneyAuthoringContext | null;
  /** 关闭旅程后仍可用于「+ 能力」再开的最近创作上下文 */
  lastJourneyAuthoring: JourneyAuthoringContext | null;
  startJourneyAuthoringSession: (ctx: JourneyAuthoringContext) => Promise<void>;
  /** 退出旅程能力：清 mode，不删用户消息 */
  stopJourneyAuthoring: () => void;
  brandSiteAuthoring: BrandSiteAuthoringContext | null;
  /** 关闭后仍可用于「+ 能力」再开 */
  lastBrandSiteAuthoring: BrandSiteAuthoringContext | null;
  /** 进入编辑页时记下上下文，不打开读写能力 */
  prepareBrandSiteAuthoring: (ctx: BrandSiteAuthoringContext) => void;
  /** 打开「AI 官网」：给当前对话加读写能力，不换会话、不自动发消息（非工作室主路径） */
  startBrandSiteAuthoringSession: (
    ctx: BrandSiteAuthoringContext,
  ) => Promise<{ reused: boolean }>;
  stopBrandSiteAuthoring: () => void;
  brandSiteSessionReady: boolean;
  /** 设计工作室：右栏场景会话（默认同站最近一条，可新建/切换） */
  brandSiteStudioActive: boolean;
  startBrandSiteStudioSession: (
    ctx: BrandSiteAuthoringContext & {
      kbBindings?: KbWikiBinding[];
      enterpriseWikiId?: number | null;
    },
    options?: { forceNew?: boolean },
  ) => Promise<{ reused: boolean; syncFailed?: boolean }>;
  switchBrandSiteStudioSession: (chatId: number) => Promise<void>;
  restartBrandSiteStudioSession: () => Promise<{ ok: boolean }>;
  stopBrandSiteStudio: () => void;
  /** 发布页可读性优化：固定场景会话（find-or-create，不进入通用会话）。 */
  brandSiteScoreOptimizeActive: boolean;
  startBrandSiteScoreOptimizeSession: (
    ctx: BrandSiteScoreOptimizeContext,
    options?: { composerSeed?: string; forceNew?: boolean },
  ) => Promise<{ reused: boolean; syncFailed?: boolean }>;
  stopBrandSiteScoreOptimize: () => void;
  /** 可读性优化：新建空会话（同站固定场景，不进通用列表）。 */
  restartBrandSiteScoreOptimizeSession: (options?: {
    composerSeed?: string;
  }) => Promise<{ ok: boolean }>;
  /** 打开可读性优化时一次性预填输入框（Drawer 消费后清空）。 */
  composerSeed: { id: number; text: string } | null;
  clearComposerSeed: () => void;
  /** 平台模板 IDE（/platform/templates/.../ide） */
  platformTemplateIde: PlatformTemplateIdeContext | null;
  lastPlatformTemplateIde: PlatformTemplateIdeContext | null;
  preparePlatformTemplateIde: (ctx: PlatformTemplateIdeContext) => void;
  startPlatformTemplateIdeSession: (
    ctx: PlatformTemplateIdeContext,
  ) => Promise<{ reused: boolean; syncFailed?: boolean }>;
  stopPlatformTemplateIde: () => void;
  /** Cable `chat_updated` 推送的标题（任意会话）；侧栏优先合并展示 */
  sessionTitles: Record<number, string>;
  /**
   * brand_site_write_page / delete / apply_preview 的 tool_result 后递增；
   * 设计页 / 模板 IDE 据此刷新草稿预览（替代空闲轮询）。
   */
  brandSiteDraftRevision: number;
  /** 最近一次「写入草稿」卡片带来的乐观更新提示（可与 revision 同拍） */
  brandSiteDraftMutationHint: BrandSiteDraftMutationHint | null;
  notifyBrandSiteDraftMutated: (hint?: BrandSiteDraftMutationHint | null) => void;
  clearBrandSiteDraftMutationHint: () => void;
};

const HermesContext = createContext<HermesContextValue | null>(null);

export function useHermes() {
  const context = useContext(HermesContext);
  if (!context) {
    throw new Error("useHermes must be used within HermesProvider");
  }
  return context;
}

export function HermesProvider({
  children,
  accountIdOverride,
}: {
  children?: ReactNode;
  /** 平台 IDE 等无工作区场景：强制 Hermes 账户（如 PLATFORM 模板站 account_id） */
  accountIdOverride?: string;
}) {
  const workspace = useOptionalAccountWorkspace();
  const accountId =
    (accountIdOverride && accountIdOverride.trim()) ||
    workspace?.accountId ||
    readRememberedAccountId() ||
    "";

  const [open, setOpenState] = useState(false);
  /** 全宽态在关闭后仍保留，再次打开时由 WorkspaceShell 按 expanded 直接以浮层进场。 */
  const [expanded, setExpanded] = useState(false);
  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
  }, []);
  const [chatId, setChatId] = useState<number | null>(null);
  const [chatSkills, setChatSkills] = useState<HermesSkillId[]>([]);
  const [draftSkills, setDraftSkillsState] = useState<HermesSkillId[]>([]);
  const [draftKbBindings, setDraftKbBindingsState] = useState<KbWikiBinding[]>([]);
  const [draftMaterials, setDraftMaterialsState] = useState<MaterialsBinding[]>([]);
  const [draftJourney, setDraftJourneyState] = useState<BrandToolBinding[]>([]);
  const [draftBrandSite, setDraftBrandSiteState] = useState<BrandToolBinding[]>([]);
  const [draftFetchOn, setDraftFetchOnState] = useState(true);
  const [messages, setMessages] = useState<HermesMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [queuedMessages, setQueuedMessages] = useState<HermesQueuedMessage[]>([]);
  const [isSwitchingSession, setIsSwitchingSession] = useState(false);
  const [composerFocusNonce, setComposerFocusNonce] = useState(0);
  const [streamStatus, setStreamStatus] = useState<string | null>(null);
  const [knowledgeEntry, setKnowledgeEntry] = useState<KnowledgeEntryContext | null>(null);
  const [journeyAuthoring, setJourneyAuthoring] = useState<JourneyAuthoringContext | null>(null);
  const [lastJourneyAuthoring, setLastJourneyAuthoring] = useState<JourneyAuthoringContext | null>(
    null,
  );
  const journeyAuthoringRef = useRef<JourneyAuthoringContext | null>(null);
  const [brandSiteAuthoring, setBrandSiteAuthoring] = useState<BrandSiteAuthoringContext | null>(
    null,
  );
  const [lastBrandSiteAuthoring, setLastBrandSiteAuthoring] =
    useState<BrandSiteAuthoringContext | null>(null);
  const brandSiteAuthoringRef = useRef<BrandSiteAuthoringContext | null>(null);
  const [brandSiteSessionReady, setBrandSiteSessionReady] = useState(false);
  const [brandSiteStudioActive, setBrandSiteStudioActive] = useState(false);
  const brandSiteStudioActiveRef = useRef(false);
  const [brandSiteScoreOptimizeActive, setBrandSiteScoreOptimizeActive] = useState(false);
  const brandSiteScoreOptimizeActiveRef = useRef(false);
  const brandSiteScoreOptimizeRef = useRef<BrandSiteScoreOptimizeContext | null>(null);
  const brandSiteScoreOptimizeGenRef = useRef(0);
  const [composerSeed, setComposerSeed] = useState<{ id: number; text: string } | null>(null);
  const composerSeedIdRef = useRef(0);
  const [platformTemplateIde, setPlatformTemplateIde] =
    useState<PlatformTemplateIdeContext | null>(null);
  const [lastPlatformTemplateIde, setLastPlatformTemplateIde] =
    useState<PlatformTemplateIdeContext | null>(null);
  const platformTemplateIdeRef = useRef<PlatformTemplateIdeContext | null>(null);
  const [cableReconnectGen, setCableReconnectGen] = useState(0);
  const [sessionTitles, setSessionTitles] = useState<Record<number, string>>({});
  const [brandSiteDraftRevision, setBrandSiteDraftRevision] = useState(0);
  const [brandSiteDraftMutationHint, setBrandSiteDraftMutationHint] =
    useState<BrandSiteDraftMutationHint | null>(null);
  const notifyBrandSiteDraftMutated = useCallback((hint?: BrandSiteDraftMutationHint | null) => {
    setBrandSiteDraftMutationHint(hint ?? null);
    setBrandSiteDraftRevision((n) => n + 1);
  }, []);
  const clearBrandSiteDraftMutationHint = useCallback(() => {
    setBrandSiteDraftMutationHint(null);
  }, []);

  const openRef = useRef(open);
  const accountIdRef = useRef(accountId);
  const chatIdRef = useRef(chatId);
  const isSendingRef = useRef(isSending);
  const pendingUserMessageIdRef = useRef<number | null>(null);
  const lastSubmittedPromptRef = useRef<string | null>(null);
  const messagesRef = useRef(messages);
  const subscriptionRef = useRef<HermesCableSubscription | null>(null);
  const draftSkillsRef = useRef(draftSkills);
  const draftKbBindingsRef = useRef(draftKbBindings);
  const draftMaterialsRef = useRef(draftMaterials);
  const draftJourneyRef = useRef(draftJourney);
  const draftBrandSiteRef = useRef(draftBrandSite);
  const draftFetchOnRef = useRef(draftFetchOn);
  const kbBindingsUpdateQueueRef = useRef<Promise<void>>(Promise.resolve());
  /** 进行中的默认/知识录入播种；createChat 前须等它结束，避免丢掉企业 Wiki。 */
  const draftSeedPromiseRef = useRef(Promise.resolve());
  /** After Stop: ignore mid-stream events until terminal so UI stays unlocked. */
  const ignoreStreamUntilTerminalRef = useRef(false);
  /** Stop 后暂停自动出队，直到用户再发 / 插队发送。 */
  const pauseAutoDrainRef = useRef(false);
  const drainInFlightRef = useRef(false);
  const queuedMessagesRef = useRef(queuedMessages);
  /** 最近一次本会话流事件时间；长时间无事件则本地收尾，避免永久卡在「整理中」。 */
  const lastStreamActivityAtRef = useRef(0);

  openRef.current = open;
  accountIdRef.current = accountId;
  chatIdRef.current = chatId;
  isSendingRef.current = isSending;
  messagesRef.current = messages;
  queuedMessagesRef.current = queuedMessages;
  draftSkillsRef.current = draftSkills;
  draftKbBindingsRef.current = draftKbBindings;
  draftMaterialsRef.current = draftMaterials;
  draftJourneyRef.current = draftJourney;
  draftBrandSiteRef.current = draftBrandSite;
  draftFetchOnRef.current = draftFetchOn;
  journeyAuthoringRef.current = journeyAuthoring;
  brandSiteAuthoringRef.current = brandSiteAuthoring;
  platformTemplateIdeRef.current = platformTemplateIde;
  brandSiteStudioActiveRef.current = brandSiteStudioActive;
  brandSiteScoreOptimizeActiveRef.current = brandSiteScoreOptimizeActive;

  /** 作废进行中的 switchSession，避免「新对话」与慢切换竞态互相覆盖。 */
  const sessionNavGenRef = useRef(0);

  const snapshotDraft = useCallback((): CapabilityDraft => {
    return {
      fetchOn: draftFetchOnRef.current,
      materials: draftMaterialsRef.current,
      kbBindings: draftKbBindingsRef.current,
      journey: draftJourneyRef.current,
      brandSite: draftBrandSiteRef.current,
    };
  }, []);

  const applyLocalDraft = useCallback((draft: CapabilityDraft, baseSkills?: string[]) => {
    const nextBindings = parseKbBindingsFromContext({ kb_bindings: draft.kbBindings });
    const nextDraft = { ...draft, kbBindings: nextBindings };
    const persistSkills = persistSkillsForDraft(nextDraft, baseSkills ?? draftSkillsRef.current);
    const uiSkills = hermesUiSkills(persistSkills);
    draftKbBindingsRef.current = nextBindings;
    draftSkillsRef.current = uiSkills;
    draftMaterialsRef.current = nextDraft.materials;
    draftJourneyRef.current = nextDraft.journey;
    draftBrandSiteRef.current = nextDraft.brandSite;
    draftFetchOnRef.current = nextDraft.fetchOn;
    setDraftKbBindingsState(nextBindings);
    setDraftSkillsState(uiSkills);
    setDraftMaterialsState(nextDraft.materials);
    setDraftJourneyState(nextDraft.journey);
    setDraftBrandSiteState(nextDraft.brandSite);
    setDraftFetchOnState(nextDraft.fetchOn);
    return nextDraft;
  }, []);

  const persistActiveChatDraft = useCallback((draft: CapabilityDraft, extraContext?: Record<string, unknown>) => {
    const activeChatId = chatIdRef.current;
    const activeAccountId = accountIdRef.current;
    if (!activeChatId || !activeAccountId) return;

    const persistSkills = persistSkillsForDraft(draft, draftSkillsRef.current);
    kbBindingsUpdateQueueRef.current = kbBindingsUpdateQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        try {
          const { chat } = await createHermesApi(activeAccountId).updateChat(activeChatId, {
            skills: persistSkills,
            context: { ...capabilityContextFields(draft), ...extraContext },
          });
          if (chatIdRef.current === activeChatId) {
            setChatSkills(hermesUiSkills((chat.skills as string[]) ?? persistSkills));
          }
        } catch (error) {
          if (chatIdRef.current !== activeChatId) return;
          const message =
            error instanceof ApiError ? error.message : "能力范围保存失败，将在发送时重试";
          toast.error(message);
        }
      });
  }, []);

  const waitForDraftSeed = useCallback(async () => {
    for (;;) {
      const pending = draftSeedPromiseRef.current;
      await pending;
      if (draftSeedPromiseRef.current === pending) return;
    }
  }, []);

  /** 面板 PATCH 队列未完成时先等，避免 POST messages 与过期绑定互相覆盖。 */
  const waitForDraftPersist = useCallback(async () => {
    for (;;) {
      const pending = kbBindingsUpdateQueueRef.current;
      await pending.catch(() => undefined);
      if (kbBindingsUpdateQueueRef.current === pending) return;
    }
  }, []);

  const seedDefaultDraft = useCallback(
    (navGen: number) => {
      const run = async () => {
        const activeAccountId = accountIdRef.current;
        const applySeed = (draft: CapabilityDraft) => {
          if (navGen !== sessionNavGenRef.current) return;
          if (!shouldApplySeededCapabilityDraft(snapshotDraft(), draft)) return;
          const applied = applyLocalDraft(draft, []);
          if (chatIdRef.current != null) persistActiveChatDraft(applied);
        };

        if (!activeAccountId) {
          applySeed(unseededCapabilityDraft());
          return;
        }

        try {
          const { wikis } = await createKbApi(activeAccountId).listWikis();
          applySeed(
            buildDefaultCapabilityDraftFromWikis(
              wikis,
              brandIdNumberFromPath(window.location.pathname),
            ),
          );
        } catch {
          applySeed(
            buildDefaultCapabilityDraft({
              brandId: brandIdNumberFromPath(window.location.pathname),
            }),
          );
        }
      };

      const promise = run();
      draftSeedPromiseRef.current = promise;
      return promise;
    },
    [applyLocalDraft, persistActiveChatDraft, snapshotDraft],
  );

  const setDraftSkills = useCallback((skills: HermesSkillId[]) => {
    const next = skillsWithKbBindings(skills, draftKbBindingsRef.current);
    draftSkillsRef.current = next;
    setDraftSkillsState(next);
  }, []);

  const setDraftKbBindings = useCallback((bindings: KbWikiBinding[]) => {
    const nextBindings = parseKbBindingsFromContext({ kb_bindings: bindings });
    const nextDraft = applyLocalDraft({
      ...snapshotDraft(),
      kbBindings: nextBindings,
    });
    persistActiveChatDraft(nextDraft);
  }, [applyLocalDraft, persistActiveChatDraft, snapshotDraft]);

  const capabilityDraft = useMemo(
    (): CapabilityDraft => ({
      fetchOn: draftFetchOn,
      materials: draftMaterials,
      kbBindings: draftKbBindings,
      journey: draftJourney,
      brandSite: draftBrandSite,
    }),
    [draftFetchOn, draftMaterials, draftKbBindings, draftJourney, draftBrandSite],
  );

  const setCapabilityDraft = useCallback(
    (draft: CapabilityDraft, options?: { persist?: boolean }) => {
      const nextDraft = applyLocalDraft(draft);
      if (options?.persist === false) return;
      persistActiveChatDraft(nextDraft);
    },
    [applyLocalDraft, persistActiveChatDraft],
  );

  const applyLoadedChat = useCallback((chat: HermesChat, chatMessages: HermesMessage[]) => {
    const loadedDraft = capabilityDraftFromChat({
      context: chat.context,
      skills: chat.skills,
    });
    const persistSkills = persistSkillsForDraft(loadedDraft, chat.skills ?? []);
    const uiSkills = hermesUiSkills(persistSkills);
    applyLocalDraft(loadedDraft, chat.skills ?? []);
    chatIdRef.current = chat.id;
    setChatId(chat.id);
    setChatSkills(uiSkills);
    setMessages(chatMessages);
    setQueuedMessages([]);
    setKnowledgeEntry(null);
    const journeyCtx = journeyAuthoringFromChat(chat, accountIdRef.current);
    if (journeyCtx) {
      setJourneyAuthoring(journeyCtx);
      setLastJourneyAuthoring(journeyCtx);
    } else {
      setJourneyAuthoring(null);
    }
    const platformIdeCtx = platformTemplateIdeFromChat(chat, accountIdRef.current);
    if (platformIdeCtx) {
      setPlatformTemplateIde(platformIdeCtx);
      platformTemplateIdeRef.current = platformIdeCtx;
      setLastPlatformTemplateIde(platformIdeCtx);
      setBrandSiteAuthoring(null);
      brandSiteAuthoringRef.current = null;
      setBrandSiteSessionReady(false);
    } else {
      setPlatformTemplateIde(null);
      platformTemplateIdeRef.current = null;
      const brandSiteCtx = brandSiteAuthoringFromChat(chat, accountIdRef.current);
      if (brandSiteCtx) {
        setBrandSiteAuthoring(brandSiteCtx);
        brandSiteAuthoringRef.current = brandSiteCtx;
        setLastBrandSiteAuthoring(brandSiteCtx);
        setBrandSiteSessionReady(true);
      } else {
        setBrandSiteAuthoring(null);
        brandSiteAuthoringRef.current = null;
        setBrandSiteSessionReady(false);
      }
    }
    const scoreOptimizeCtx = brandSiteScoreOptimizeFromChat(chat, accountIdRef.current);
    brandSiteScoreOptimizeActiveRef.current = Boolean(scoreOptimizeCtx);
    setBrandSiteScoreOptimizeActive(Boolean(scoreOptimizeCtx));
    brandSiteScoreOptimizeRef.current = scoreOptimizeCtx;
    ignoreStreamUntilTerminalRef.current = false;
    const inFlight = hermesInFlightTurn(chatMessages);
    pendingUserMessageIdRef.current = inFlight.userMessageId;
    const lastUser = [...chatMessages].reverse().find((message) => message.role === "user");
    lastSubmittedPromptRef.current = inFlight.sending ? (lastUser?.content ?? null) : null;
    setIsSending(inFlight.sending);
    setStreamStatus(null);
    const title = chat.title?.trim();
    if (title) {
      setSessionTitles((current) =>
        current[chat.id] === title ? current : { ...current, [chat.id]: title },
      );
    }
  }, [applyLocalDraft]);

  /** 取消进行中的生成（Cable cancel）；供 Stop / 新建 / 切换会话共用。 */
  const stopGeneration = useCallback((): string | null => {
    if (!isSendingRef.current) return null;

    const activeChatId = chatIdRef.current;
    const userMessageId = pendingUserMessageIdRef.current;
    const restoreText = lastSubmittedPromptRef.current;

    if (activeChatId && userMessageId && subscriptionRef.current) {
      try {
        subscriptionRef.current.perform("cancel", {
          chat_id: activeChatId,
          user_message_id: userMessageId,
        });
      } catch {
        // Cable may be reconnecting; streamer still checks the flag once request lands.
      }
    }

    // Optimistic: drop the turn locally; Cable message_deleted / cancelled sync other windows.
    ignoreStreamUntilTerminalRef.current = true;
    pauseAutoDrainRef.current = true;
    if (userMessageId) {
      setMessages((current) => removeHermesTurn(current, userMessageId));
    }
    pendingUserMessageIdRef.current = null;
    setIsSending(false);
    setStreamStatus(null);

    return restoreText;
  }, []);

  const startNewSession = useCallback(() => {
    // 作废进行中的切换，并取消当前流，避免 UI 卡在旧会话发送态
    sessionNavGenRef.current += 1;
    const navGen = sessionNavGenRef.current;
    setIsSwitchingSession(false);
    if (isSendingRef.current) {
      stopGeneration();
    }
    setChatId(null);
    chatIdRef.current = null;
    setChatSkills([]);
    setMessages([]);
    setQueuedMessages([]);
    draftSkillsRef.current = [];
    setDraftSkillsState([]);
    applyLocalDraft(unseededCapabilityDraft(), []);
    setKnowledgeEntry(null);
    setJourneyAuthoring(null);
    setStreamStatus(null);
    pendingUserMessageIdRef.current = null;
    lastSubmittedPromptRef.current = null;
    ignoreStreamUntilTerminalRef.current = false;
    setIsSending(false);
    // 状态清空后通知抽屉聚焦输入框（含已在空会话再点「新对话」）
    setComposerFocusNonce((n) => n + 1);
    void seedDefaultDraft(navGen);
  }, [applyLocalDraft, seedDefaultDraft, stopGeneration]);

  const switchSession = useCallback(
    async (targetChatId: number) => {
      if (!accountId) {
        toast.error("请先进入账户工作区后再使用 Hermes");
        return;
      }
      if (targetChatId === chatIdRef.current) return;

      // 流式中允许切换：先取消当前生成，再加载目标会话
      if (isSendingRef.current) {
        stopGeneration();
      }

      const navGen = ++sessionNavGenRef.current;
      const hermes = createHermesApi(accountId);
      setIsSwitchingSession(true);
      try {
        const [{ chat }, { messages: chatMessages }] = await Promise.all([
          hermes.getChat(targetChatId),
          hermes.listMessages(targetChatId),
        ]);
        if (navGen !== sessionNavGenRef.current) return;
        applyLoadedChat(chat, chatMessages);
      } catch (error) {
        if (navGen !== sessionNavGenRef.current) return;
        const message = error instanceof ApiError ? error.message : "切换会话失败，请稍后重试";
        toast.error(message);
      } finally {
        if (navGen === sessionNavGenRef.current) {
          setIsSwitchingSession(false);
        }
      }
    },
    [accountId, applyLoadedChat, stopGeneration],
  );

  // 场景会话不写入通用 lastChat
  useEffect(() => {
    if (!accountId || chatId == null) return;
    if (platformTemplateIdeRef.current) return;
    if (brandSiteStudioActiveRef.current) return;
    if (brandSiteScoreOptimizeActiveRef.current) return;
    writeHermesLastChatId(accountId, chatId);
  }, [accountId, chatId]);

  // 内嵌场景期间强制关掉全局 Hermes 坞（避免误开第二聊天窗）
  useEffect(() => {
    if ((brandSiteStudioActive || brandSiteScoreOptimizeActive) && open) setOpen(false);
  }, [brandSiteScoreOptimizeActive, brandSiteStudioActive, open]);

  // 切换账户时清空侧栏标题缓存
  useEffect(() => {
    setSessionTitles({});
  }, [accountId]);

  // On open: keep in-memory chat; else last chatId (sessionStorage); else newest from list; else empty.
  // 跳过 journey_authoring / brand_site / platform_template_ide 会话，避免 FAB/客户窗误接创作对话。
  useEffect(() => {
    if (
      !open ||
      !accountId ||
      knowledgeEntry ||
      journeyAuthoring ||
      brandSiteAuthoring ||
      platformTemplateIde ||
      brandSiteScoreOptimizeActive
    ) {
      return;
    }
    if (chatIdRef.current != null) return;

    let cancelled = false;
    const navGen = sessionNavGenRef.current;

    const promise = (async () => {
      const hermes = createHermesApi(accountId);

      const tryLoad = async (targetChatId: number): Promise<boolean> => {
        try {
          const [{ chat }, { messages: chatMessages }] = await Promise.all([
            hermes.getChat(targetChatId),
            hermes.listMessages(targetChatId),
          ]);
          if (cancelled || navGen !== sessionNavGenRef.current) return true;
          if (chatIdRef.current != null) return true;
          if (
            isJourneyAuthoringChat(chat) ||
            isBrandSiteAuthoringChat(chat) ||
            isPlatformTemplateIdeChat(chat) ||
            isBrandSiteScoreOptimizeChat(chat)
          ) {
            return false;
          }
          applyLoadedChat(chat, chatMessages);
          return true;
        } catch {
          return false;
        }
      };

      const remembered = readHermesLastChatId(accountId);
      if (remembered && (await tryLoad(remembered))) return;

      try {
        const { chats } = await hermes.listChats();
        if (cancelled || navGen !== sessionNavGenRef.current) return;
        if (chatIdRef.current != null) return;
        const newest = chats.find(
          (chat) =>
            !isJourneyAuthoringChat(chat) &&
            !isBrandSiteAuthoringChat(chat) &&
            !isPlatformTemplateIdeChat(chat) &&
            !isBrandSiteScoreOptimizeChat(chat),
        );
        if (newest && (await tryLoad(newest.id))) return;
      } catch {
        // 列表失败则走空会话默认绑定
      }

      if (cancelled || navGen !== sessionNavGenRef.current) return;
      if (chatIdRef.current != null) return;
      await seedDefaultDraft(navGen);
    })();
    draftSeedPromiseRef.current = promise;

    return () => {
      cancelled = true;
    };
  }, [
    open,
    accountId,
    knowledgeEntry,
    journeyAuthoring,
    brandSiteAuthoring,
    platformTemplateIde,
    brandSiteScoreOptimizeActive,
    applyLoadedChat,
    seedDefaultDraft,
  ]);

  const startKnowledgeEntrySession = useCallback((ctx: KnowledgeEntryContext) => {
    sessionNavGenRef.current += 1;
    const navGen = sessionNavGenRef.current;
    const brandId =
      ctx.brandId != null && Number(ctx.brandId) > 0
        ? Number(ctx.brandId)
        : brandIdNumberFromPath(window.location.pathname);
    const pendingPlaceholder = applyKnowledgeEntryOverride(
      buildDefaultCapabilityDraft({ brandId }),
      { wikiId: ctx.wikiId },
    );
    applyLocalDraft(pendingPlaceholder, []);
    setJourneyAuthoring(null);
    setBrandSiteAuthoring(null);
    brandSiteAuthoringRef.current = null;
    setBrandSiteSessionReady(false);
    setPlatformTemplateIde(null);
    platformTemplateIdeRef.current = null;
    setKnowledgeEntry(ctx);
    setOpen(true);
    setStreamStatus(null);
    pendingUserMessageIdRef.current = null;

    // Prefer reusing a previous knowledge-entry chat for the same wiki (enterprise/brand stack).
    const promise = (async () => {
      if (!ctx.accountId) {
        setChatId(null);
        chatIdRef.current = null;
        setChatSkills([]);
        setMessages([]);
        return;
      }

      try {
        const hermes = createHermesApi(ctx.accountId);
        const [{ chats }, { wikis }] = await Promise.all([
          hermes.listChats(),
          createKbApi(ctx.accountId).listWikis(),
        ]);
        if (navGen !== sessionNavGenRef.current) return;
        const seeded = applyKnowledgeEntryOverride(
          buildDefaultCapabilityDraftFromWikis(wikis, brandId),
          { wikiId: ctx.wikiId },
        );
        if (shouldApplySeededCapabilityDraft(snapshotDraft(), seeded, pendingPlaceholder)) {
          applyLocalDraft(seeded, []);
        }
        const entryDraft = snapshotDraft();

        const sameWiki = chats.find((chat) => {
          const mode = chat.context?.mode;
          const wikiId = chat.context?.wiki_id;
          return mode === "knowledge_entry" && Number(wikiId) === ctx.wikiId;
        });
        const anyEntry = chats.find((chat) => chat.context?.mode === "knowledge_entry");
        const match = sameWiki ?? anyEntry;
        const persistSkills = persistSkillsForDraft(entryDraft);
        const entryContext = {
          ...capabilityContextFields(entryDraft),
          ...knowledgeEntryChatContext(ctx),
          kb_bindings: entryDraft.kbBindings,
        };

        if (!match) {
          setChatId(null);
          chatIdRef.current = null;
          setChatSkills(hermesUiSkills(persistSkills));
          setMessages([]);
          return;
        }

        if (match.id === chatIdRef.current) {
          setChatSkills(hermesUiSkills(persistSkills));
          await hermes.updateChat(match.id, {
            skills: persistSkills,
            context: entryContext,
          });
          return;
        }

        const [{ chat }, { messages: chatMessages }] = await Promise.all([
          hermes.updateChat(match.id, {
            skills: persistSkills,
            context: entryContext,
          }),
          hermes.listMessages(match.id),
        ]);
        if (navGen !== sessionNavGenRef.current) return;
        chatIdRef.current = chat.id;
        setChatId(chat.id);
        setChatSkills(hermesUiSkills(persistSkills));
        setMessages(chatMessages);
        setIsSending(false);
      } catch {
        if (navGen !== sessionNavGenRef.current) return;
        setChatId(null);
        chatIdRef.current = null;
        setChatSkills([]);
        setMessages([]);
      }
    })();
    draftSeedPromiseRef.current = promise;
  }, [applyLocalDraft, snapshotDraft]);

  const updateKnowledgeEntryStructured = useCallback((structured: Record<string, unknown>) => {
    setKnowledgeEntry((current) => (current ? { ...current, structured } : current));
  }, []);

  const startJourneyAuthoringSession = useCallback(async (ctx: JourneyAuthoringContext) => {
    // 设计工作室已占用会话壳：禁止切到 journey_authoring / 打开全局抽屉
    if (brandSiteStudioActiveRef.current) return;

    // 创作会话不强制 kb skill / kb_bindings
    setKnowledgeEntry(null);
    setBrandSiteAuthoring(null);
    brandSiteAuthoringRef.current = null;
    setBrandSiteSessionReady(false);
    setPlatformTemplateIde(null);
    platformTemplateIdeRef.current = null;
    setJourneyAuthoring(ctx);
    setLastJourneyAuthoring(ctx);
    setOpen(true);
    setStreamStatus(null);
    pendingUserMessageIdRef.current = null;

    if (!ctx.accountId) {
      setChatId(null);
      chatIdRef.current = null;
      setChatSkills([]);
      setMessages([]);
      return;
    }

    try {
      const hermes = createHermesApi(ctx.accountId);
      const { chats } = await hermes.listChats();
      const match = chats.find((chat) => matchesJourneyAuthoringChat(chat, ctx));
      // 绑定跟当前草稿走，mode 后写以免被覆盖
      const authoringContext = withJourneyAuthoringMode(
        snapshotDraft(),
        journeyAuthoringChatContext(ctx),
      );

      if (!match) {
        setChatId(null);
        chatIdRef.current = null;
        setChatSkills([]);
        setMessages([]);
        return;
      }

      if (match.id === chatIdRef.current) {
        await hermes.updateChat(match.id, {
          context: authoringContext,
        });
        return;
      }

      const [{ chat }, { messages: chatMessages }] = await Promise.all([
        hermes.updateChat(match.id, {
          context: authoringContext,
        }),
        hermes.listMessages(match.id),
      ]);
      const bindings = parseKbBindingsFromContext(chat.context);
      const skills = skillsWithKbBindings((chat.skills as HermesSkillId[]) ?? [], bindings);
      chatIdRef.current = chat.id;
      setChatId(chat.id);
      setChatSkills(skills);
      draftSkillsRef.current = skills;
      setDraftSkillsState(skills);
      draftKbBindingsRef.current = bindings;
      setDraftKbBindingsState(bindings);
      setMessages(chatMessages);
      setIsSending(false);
    } catch {
      setChatId(null);
      chatIdRef.current = null;
      setChatSkills([]);
      setMessages([]);
    }
  }, [snapshotDraft]);

  const prepareBrandSiteAuthoring = useCallback((ctx: BrandSiteAuthoringContext) => {
    setLastBrandSiteAuthoring(ctx);
  }, []);

  const startBrandSiteAuthoringSession = useCallback(
    async (ctx: BrandSiteAuthoringContext): Promise<{ reused: boolean }> => {
      setKnowledgeEntry(null);
      setJourneyAuthoring(null);
      journeyAuthoringRef.current = null;
      setPlatformTemplateIde(null);
      platformTemplateIdeRef.current = null;
      setBrandSiteAuthoring(ctx);
      brandSiteAuthoringRef.current = ctx;
      setLastBrandSiteAuthoring(ctx);
      setBrandSiteSessionReady(true);
      setOpen(true);

      const activeChatId = chatIdRef.current;
      if (!activeChatId || !ctx.accountId) return { reused: false };

      try {
        await createHermesApi(ctx.accountId).updateChat(activeChatId, {
          context: withBrandSiteAuthoringMode(
            snapshotDraft(),
            brandSiteAuthoringChatContext(ctx),
          ),
        });
        return { reused: true };
      } catch {
        return { reused: false };
      }
    },
    [snapshotDraft],
  );

  const stopBrandSiteAuthoring = useCallback(() => {
    const current = brandSiteAuthoringRef.current;
    if (current) setLastBrandSiteAuthoring(current);
    brandSiteAuthoringRef.current = null;
    setBrandSiteAuthoring(null);
    setBrandSiteSessionReady(false);
    brandSiteStudioActiveRef.current = false;
    setBrandSiteStudioActive(false);

    const activeChatId = chatIdRef.current;
    const activeAccountId = accountIdRef.current;
    if (!activeChatId || !activeAccountId) return;

    void createHermesApi(activeAccountId)
      .updateChat(activeChatId, { context: clearBrandSiteAuthoringModeContext() })
      .catch((error) => {
        if (chatIdRef.current !== activeChatId) return;
        const message =
          error instanceof ApiError ? error.message : "关闭官网创作失败，请稍后重试";
        toast.error(message);
      });
  }, []);

  const startBrandSiteStudioSession = useCallback(
    async (
      ctx: BrandSiteAuthoringContext & {
        kbBindings?: KbWikiBinding[];
        enterpriseWikiId?: number | null;
      },
      options?: { forceNew?: boolean },
    ): Promise<{ reused: boolean; syncFailed?: boolean }> => {
      setKnowledgeEntry(null);
      setJourneyAuthoring(null);
      journeyAuthoringRef.current = null;
      setPlatformTemplateIde(null);
      platformTemplateIdeRef.current = null;
      setBrandSiteAuthoring(ctx);
      brandSiteAuthoringRef.current = ctx;
      setLastBrandSiteAuthoring(ctx);
      setBrandSiteSessionReady(true);
      brandSiteStudioActiveRef.current = true;
      setBrandSiteStudioActive(true);
      setOpen(false);

      const lockDraft = buildBrandSiteStudioLockedDraft({
        brandId: ctx.brandId,
        brandSiteId: ctx.brandSiteId,
        enterpriseWikiId: ctx.enterpriseWikiId,
        kbBindings: ctx.kbBindings,
      });
      const nextSkills = brandSiteStudioSkills(lockDraft, draftSkillsRef.current);
      applyLocalDraft(lockDraft, nextSkills);
      setChatSkills(nextSkills as HermesSkillId[]);

      if (!ctx.accountId) return { reused: false };

      const hermes = createHermesApi(ctx.accountId);
      const chatContext = buildBrandSiteStudioChatContext(ctx, {
        enterpriseWikiId: ctx.enterpriseWikiId,
        kbBindings: ctx.kbBindings,
      });

      try {
        if (!options?.forceNew) {
          const { chats } = await hermes.listChats({
            mode: "brand_site_authoring",
            brand_site_id: ctx.brandSiteId,
          });
          const existing = chats[0];
          if (existing) {
            const [{ chat }, { messages: chatMessages }] = await Promise.all([
              hermes.getChat(existing.id),
              hermes.listMessages(existing.id),
            ]);
            applyLoadedChat(chat, chatMessages);
            await hermes.updateChat(existing.id, {
              skills: nextSkills,
              context: chatContext,
            });
            applyLocalDraft(lockDraft, nextSkills);
            return { reused: true };
          }
        }

        const { chat } = await hermes.createChat(nextSkills, chatContext);
        applyLoadedChat(chat, []);
        applyLocalDraft(lockDraft, nextSkills);
        return { reused: false };
      } catch (error) {
        const message =
          error instanceof ApiError ? error.message : "打开官网设计会话失败，请稍后重试";
        toast.error(message);
        return { reused: false, syncFailed: true };
      }
    },
    [applyLoadedChat, applyLocalDraft],
  );

  const switchBrandSiteStudioSession = useCallback(
    async (targetChatId: number) => {
      const ctx = brandSiteAuthoringRef.current;
      if (!ctx?.accountId) {
        toast.error("请先进入设计工作室");
        return;
      }
      if (targetChatId === chatIdRef.current) return;

      if (isSendingRef.current) stopGeneration();

      const navGen = ++sessionNavGenRef.current;
      const hermes = createHermesApi(ctx.accountId);
      setIsSwitchingSession(true);
      try {
        const [{ chat }, { messages: chatMessages }] = await Promise.all([
          hermes.getChat(targetChatId),
          hermes.listMessages(targetChatId),
        ]);
        if (navGen !== sessionNavGenRef.current) return;

        const chatCtx = chat.context ?? {};
        const mode = typeof chatCtx.mode === "string" ? chatCtx.mode : "";
        const siteId = Number(chatCtx.brand_site_id);
        if (mode !== "brand_site_authoring" || siteId !== ctx.brandSiteId) {
          toast.error("该会话不属于当前官网设计场景");
          return;
        }

        applyLoadedChat(chat, chatMessages);
        const loadedDraft = capabilityDraftFromChat({
          context: chat.context,
          skills: chat.skills,
        });
        const lockDraft = buildBrandSiteStudioLockedDraft({
          brandId: ctx.brandId,
          brandSiteId: ctx.brandSiteId,
        });
        const mergedDraft = mergeBrandSiteStudioFloor(loadedDraft, lockDraft);
        const nextSkills = brandSiteStudioSkills(mergedDraft, chat.skills ?? []);
        applyLocalDraft(mergedDraft, nextSkills);
        setChatSkills(nextSkills as HermesSkillId[]);
      } catch (error) {
        if (navGen !== sessionNavGenRef.current) return;
        const message = error instanceof ApiError ? error.message : "切换会话失败，请稍后重试";
        toast.error(message);
      } finally {
        if (navGen === sessionNavGenRef.current) setIsSwitchingSession(false);
      }
    },
    [applyLoadedChat, applyLocalDraft, stopGeneration],
  );

  const restartBrandSiteStudioSession = useCallback(async (): Promise<{ ok: boolean }> => {
    const ctx = brandSiteAuthoringRef.current;
    if (!ctx) {
      toast.error("请先打开设计工作室");
      return { ok: false };
    }
    if (isSendingRef.current) stopGeneration();
    const result = await startBrandSiteStudioSession(ctx, { forceNew: true });
    if (result.syncFailed) return { ok: false };
    toast.success("已开始新对话");
    return { ok: true };
  }, [startBrandSiteStudioSession, stopGeneration]);

  const stopBrandSiteStudio = useCallback(() => {
    const current = brandSiteAuthoringRef.current;
    if (current) setLastBrandSiteAuthoring(current);
    brandSiteAuthoringRef.current = null;
    setBrandSiteAuthoring(null);
    setBrandSiteSessionReady(false);
    brandSiteStudioActiveRef.current = false;
    setBrandSiteStudioActive(false);
    chatIdRef.current = null;
    setChatId(null);
    setMessages([]);
    setQueuedMessages([]);
  }, []);

  const stopBrandSiteScoreOptimize = useCallback(() => {
    if (!brandSiteScoreOptimizeActiveRef.current) return;
    brandSiteScoreOptimizeGenRef.current += 1;
    brandSiteScoreOptimizeActiveRef.current = false;
    setBrandSiteScoreOptimizeActive(false);
    brandSiteScoreOptimizeRef.current = null;
    setComposerSeed(null);
    chatIdRef.current = null;
    setChatId(null);
    setMessages([]);
    setQueuedMessages([]);
  }, []);

  const clearComposerSeed = useCallback(() => {
    setComposerSeed(null);
  }, []);

  const startBrandSiteScoreOptimizeSession = useCallback(
    async (
      ctx: BrandSiteScoreOptimizeContext,
      options?: { composerSeed?: string; forceNew?: boolean },
    ): Promise<{ reused: boolean; syncFailed?: boolean }> => {
      if (shouldReplaceBrandSiteScoreOptimize(brandSiteScoreOptimizeRef.current, ctx.brandSiteId)) {
        stopBrandSiteScoreOptimize();
      }
      const startGeneration = ++brandSiteScoreOptimizeGenRef.current;
      const acceptsStartResult = () =>
        shouldAcceptBrandSiteScoreOptimizeStart(
          brandSiteScoreOptimizeActiveRef.current,
          brandSiteScoreOptimizeRef.current,
          brandSiteScoreOptimizeGenRef.current,
          startGeneration,
          ctx.brandSiteId,
        );

      setKnowledgeEntry(null);
      setJourneyAuthoring(null);
      journeyAuthoringRef.current = null;
      setBrandSiteAuthoring(null);
      brandSiteAuthoringRef.current = null;
      setBrandSiteSessionReady(false);
      brandSiteStudioActiveRef.current = false;
      setBrandSiteStudioActive(false);
      setPlatformTemplateIde(null);
      platformTemplateIdeRef.current = null;
      brandSiteScoreOptimizeRef.current = ctx;
      brandSiteScoreOptimizeActiveRef.current = true;
      setBrandSiteScoreOptimizeActive(true);
      setOpen(false);

      const emptyDraft = unseededCapabilityDraft();
      applyLocalDraft(emptyDraft, []);
      setChatSkills([]);

      if (!ctx.accountId) {
        brandSiteScoreOptimizeActiveRef.current = false;
        setBrandSiteScoreOptimizeActive(false);
        brandSiteScoreOptimizeRef.current = null;
        return { reused: false, syncFailed: true };
      }

      const hermes = createHermesApi(ctx.accountId);
      const chatContext = brandSiteScoreOptimizeChatContext(ctx);

      const applyComposerSeed = () => {
        const seed = options?.composerSeed?.trim();
        if (!seed) return;
        composerSeedIdRef.current += 1;
        setComposerSeed({ id: composerSeedIdRef.current, text: seed });
        setComposerFocusNonce((n) => n + 1);
      };

      try {
        if (!options?.forceNew) {
          const { chats } = await hermes.listChats({
            mode: "brand_site_score_optimize",
            brand_site_id: ctx.brandSiteId,
          });
          if (!acceptsStartResult()) return { reused: false, syncFailed: true };
          const existing = chats[0];
          if (existing) {
            const [{ chat }, { messages: chatMessages }] = await Promise.all([
              hermes.getChat(existing.id),
              hermes.listMessages(existing.id),
            ]);
            if (!acceptsStartResult()) return { reused: false, syncFailed: true };
            const { chat: updatedChat } = await hermes.updateChat(existing.id, {
              skills: [],
              context: chatContext,
            });
            if (!acceptsStartResult()) return { reused: false, syncFailed: true };
            applyLoadedChat(updatedChat, chatMessages);
            applyLocalDraft(emptyDraft, []);
            applyComposerSeed();
            return { reused: true };
          }
        }

        const { chat } = await hermes.createChat([], chatContext);
        if (!acceptsStartResult()) return { reused: false, syncFailed: true };
        applyLoadedChat(chat, []);
        applyLocalDraft(emptyDraft, []);
        applyComposerSeed();
        return { reused: false };
      } catch (error) {
        if (!acceptsStartResult()) return { reused: false, syncFailed: true };
        brandSiteScoreOptimizeActiveRef.current = false;
        setBrandSiteScoreOptimizeActive(false);
        brandSiteScoreOptimizeRef.current = null;
        const message =
          error instanceof ApiError ? error.message : "打开可读性优化会话失败，请稍后重试";
        toast.error(message);
        return { reused: false, syncFailed: true };
      }
    },
    [applyLoadedChat, applyLocalDraft, stopBrandSiteScoreOptimize],
  );

  const restartBrandSiteScoreOptimizeSession = useCallback(
    async (options?: { composerSeed?: string }): Promise<{ ok: boolean }> => {
      const ctx = brandSiteScoreOptimizeRef.current;
      if (!ctx) {
        toast.error("请先打开可读性优化");
        return { ok: false };
      }
      if (isSendingRef.current) stopGeneration();
      const result = await startBrandSiteScoreOptimizeSession(ctx, {
        composerSeed: options?.composerSeed,
        forceNew: true,
      });
      if (result.syncFailed) return { ok: false };
      toast.success("已开始新对话");
      return { ok: true };
    },
    [startBrandSiteScoreOptimizeSession, stopGeneration],
  );

  const preparePlatformTemplateIde = useCallback((ctx: PlatformTemplateIdeContext) => {
    setLastPlatformTemplateIde(ctx);
  }, []);

  const startPlatformTemplateIdeSession = useCallback(
    async (
      ctx: PlatformTemplateIdeContext,
    ): Promise<{ reused: boolean; syncFailed?: boolean }> => {
      setKnowledgeEntry(null);
      setJourneyAuthoring(null);
      journeyAuthoringRef.current = null;
      setBrandSiteAuthoring(null);
      brandSiteAuthoringRef.current = null;
      setBrandSiteSessionReady(false);
      brandSiteStudioActiveRef.current = false;
      setBrandSiteStudioActive(false);
      setPlatformTemplateIde(ctx);
      platformTemplateIdeRef.current = ctx;
      setLastPlatformTemplateIde(ctx);
      draftKbBindingsRef.current = ctx.kbBindings;
      setDraftKbBindingsState(ctx.kbBindings);
      const nextSkills = skillsWithKbBindings(draftSkillsRef.current, ctx.kbBindings);
      draftSkillsRef.current = nextSkills;
      setDraftSkillsState(nextSkills);
      setOpen(true);

      if (!ctx.accountId) return { reused: false };

      const hermes = createHermesApi(ctx.accountId);
      const chatContext = platformTemplateIdeChatContext(ctx);

      try {
        const { chats } = await hermes.listChats({
          mode: "platform_template_ide",
          brand_site_id: ctx.brandSiteId,
        });
        const existing = chats[0];
        if (existing) {
          const [{ chat }, { messages: chatMessages }] = await Promise.all([
            hermes.getChat(existing.id),
            hermes.listMessages(existing.id),
          ]);
          applyLoadedChat(chat, chatMessages);
          await hermes.updateChat(existing.id, {
            skills: nextSkills,
            context: chatContext,
          });
          draftKbBindingsRef.current = ctx.kbBindings;
          setDraftKbBindingsState(ctx.kbBindings);
          setChatSkills(nextSkills);
          draftSkillsRef.current = nextSkills;
          setDraftSkillsState(nextSkills);
          setPlatformTemplateIde(ctx);
          platformTemplateIdeRef.current = ctx;
          return { reused: true };
        }

        const { chat } = await hermes.createChat(nextSkills, chatContext);
        applyLoadedChat(chat, []);
        setPlatformTemplateIde(ctx);
        platformTemplateIdeRef.current = ctx;
        return { reused: false };
      } catch (error) {
        const message =
          error instanceof ApiError ? error.message : "打开模板 IDE 会话失败，请稍后重试";
        toast.error(message);
        return { reused: false, syncFailed: true };
      }
    },
    [applyLoadedChat],
  );

  const stopPlatformTemplateIde = useCallback(() => {
    const current = platformTemplateIdeRef.current;
    if (current) setLastPlatformTemplateIde(current);
    platformTemplateIdeRef.current = null;
    setPlatformTemplateIde(null);
    // 场景会话保留 context.mode，不写回通用列表；离开 IDE 时卸掉本 Provider 内会话指针即可
    chatIdRef.current = null;
    setChatId(null);
    setMessages([]);
    setQueuedMessages([]);
  }, []);

  /** 关旅程 chip / 面板开关：退出 mode，保留消息与 brand/journey 便于再开。 */
  const stopJourneyAuthoring = useCallback(() => {
    const current = journeyAuthoringRef.current;
    if (current) setLastJourneyAuthoring(current);
    journeyAuthoringRef.current = null;
    setJourneyAuthoring(null);

    const activeChatId = chatIdRef.current;
    const activeAccountId = accountIdRef.current;
    if (!activeChatId || !activeAccountId) return;

    void createHermesApi(activeAccountId)
      .updateChat(activeChatId, { context: clearJourneyAuthoringModeContext() })
      .catch((error) => {
        if (chatIdRef.current !== activeChatId) return;
        const message =
          error instanceof ApiError ? error.message : "关闭旅程能力失败，请稍后重试";
        toast.error(message);
      });
  }, []);

  const catchUpStream = useCallback(async (activeAccountId: string, activeChatId: number, afterId?: number) => {
    try {
      // 生成中且无 after_id 时跳过：全量 buffer 可能含旧轮 done，会误解锁发送态。
      if (isSendingRef.current && afterId == null) return;

      const hermes = createHermesApi(activeAccountId);
      const { events, messages: streamMessages } = await hermes.pollStream(activeChatId, afterId);
      if (streamMessages.length > 0 || events.length > 0) {
        lastStreamActivityAtRef.current = Date.now();
        setMessages((current) => foldHermesStreamCatchUp(current, streamMessages, events));
      }
      const statusEvent = [...events]
        .reverse()
        .find(
          (event) =>
            event.type === "tool_result" ||
            event.type === "tool_call" ||
            event.type === "context_compacting" ||
            event.type === "karpathy_wiki" ||
            event.type === "brand_site_authoring",
        );
      const toolStatus = statusEvent ? statusFromStreamEvent(statusEvent) : null;
      if (toolStatus) {
        setStreamStatus(toolStatus);
      } else if (
        (statusEvent?.type === "karpathy_wiki" || statusEvent?.type === "brand_site_authoring") &&
        statusEvent.status === "done"
      ) {
        setStreamStatus(null);
      }
      if (
        events.some(
          (event) =>
            event.type === "tool_result" && isBrandSiteDraftMutatingToolName(event.delta),
        )
      ) {
        // 工具写页以服务端为准；清掉卡片乐观 hint，避免盖住新稿
        setBrandSiteDraftMutationHint(null);
        setBrandSiteDraftRevision((n) => n + 1);
      }
      if (hermesStreamEventsUnlockSend(events, pendingUserMessageIdRef.current)) {
        pendingUserMessageIdRef.current = null;
        setIsSending(false);
        setStreamStatus(null);
      }
    } catch {
      // Catch-up is best-effort; live Cable remains primary.
    }
  }, []);

  // 刷新后 isSending 从消息恢复时，立刻补洞并接上思考状态。
  useEffect(() => {
    if (!isSending || chatId == null || !accountId) return;
    const afterId = pendingUserMessageIdRef.current;
    if (afterId == null) return;
    void catchUpStream(accountId, chatId, afterId);
  }, [isSending, chatId, accountId, catchUpStream]);

  // 流静默过久：先补洞；仍无终态则本地解锁并提示失败（后端挂死/进程中断时 Cable 不会来 done）。
  useEffect(() => {
    if (!isSending) return;

    const STREAM_STALL_MS = 10 * 60 * 1000;
    const TICK_MS = 30_000;
    lastStreamActivityAtRef.current = Date.now();

    const timer = window.setInterval(() => {
      if (!isSendingRef.current) return;
      if (Date.now() - lastStreamActivityAtRef.current < STREAM_STALL_MS) return;

      const activeChatId = chatIdRef.current;
      const activeAccountId = accountIdRef.current;
      if (!activeChatId || !activeAccountId) return;

      void (async () => {
        await catchUpStream(
          activeAccountId,
          activeChatId,
          pendingUserMessageIdRef.current ?? undefined,
        );
        if (!isSendingRef.current) return;
        if (Date.now() - lastStreamActivityAtRef.current < STREAM_STALL_MS) return;

        pendingUserMessageIdRef.current = null;
        setIsSending(false);
        setStreamStatus(null);
        const failLine = "这次执行失败。请稍后重试。";
        setMessages((current) => {
          const last = current[current.length - 1];
          if (!last || last.role !== "assistant") return current;
          if (last.tool_payload?.error) return current;
          if (last.content.includes(failLine)) return current;
          const nextContent = last.content.trim()
            ? `${last.content.trimEnd()}\n\n---\n\n${failLine}`
            : failLine;
          return current.map((message, index) =>
            index === current.length - 1
              ? {
                  ...message,
                  content: nextContent,
                  tool_payload: { ...(message.tool_payload ?? {}), error: "generic" },
                }
              : message,
          );
        });
        toast.error(failLine);
      })();
    }, TICK_MS);

    return () => window.clearInterval(timer);
  }, [isSending, catchUpStream]);

  useEffect(() => {
    registerCableIdleChecker(() => !isSendingRef.current);
    return () => registerCableIdleChecker(null);
  }, []);

  useEffect(() => subscribeCableReconnect(() => setCableReconnectGen((gen) => gen + 1)), []);

  // Shell-level subscription: lives for the workspace lifetime, independent of drawer open/close.
  useEffect(() => {
    if (!accountId) return;

    const consumer = cableConsumer();
    if (!consumer) return;

    const subscription = consumer.subscriptions.create(
      { channel: "HermesStreamChannel" },
      {
        connected() {
          const activeChatId = chatIdRef.current;
          const activeAccountId = accountIdRef.current;
          if (activeChatId && activeAccountId && isSendingRef.current) {
            void catchUpStream(activeAccountId, activeChatId, pendingUserMessageIdRef.current ?? undefined);
          }
        },
        received(data: unknown) {
          const payload = parseHermesCablePayload(data);
          if (!payload) return;

          const activeAccountId = accountIdRef.current;
          if (activeAccountId && String(payload.account_id) !== String(activeAccountId)) {
            return;
          }

          if (chatIdRef.current !== null && payload.chat_id === chatIdRef.current) {
            lastStreamActivityAtRef.current = Date.now();
          }

          // 任意会话的标题更新都写入侧栏映射（不要求当前打开该会话）
          if (payload.event.type === "chat_updated") {
            const nextTitle = payload.event.title?.trim();
            if (nextTitle) {
              setSessionTitles((current) =>
                current[payload.chat_id] === nextTitle
                  ? current
                  : { ...current, [payload.chat_id]: nextTitle },
              );
            }
          } else if (
            payload.event.type === "done" &&
            typeof payload.event.title === "string" &&
            payload.event.title.trim()
          ) {
            const nextTitle = payload.event.title.trim();
            setSessionTitles((current) =>
              current[payload.chat_id] === nextTitle
                ? current
                : { ...current, [payload.chat_id]: nextTitle },
            );
          }

          const activeChatId = chatIdRef.current;
          const forActiveChat = activeChatId !== null && payload.chat_id === activeChatId;

          // After Stop, ignore mid-stream snapshots/deltas so a late event cannot re-add a deleted turn.
          // Still accept a new user message / retry kickoff from another window.
          const allowWhileIgnoring =
            payload.event.type === "message_created" ||
            payload.event.type === "retry_started";
          const acceptStreamUpdate =
            forActiveChat && (!ignoreStreamUntilTerminalRef.current || allowWhileIgnoring);

          // 含 message 的事件（尤其 done）：用服务端 as_api_json 全量替换该条，
          // 覆盖流式 delta 拼接的中间态（规范化 Markdown、sources、proposals）。
          if (acceptStreamUpdate && payload.message) {
            setMessages((current) => mergeHermesMessages(current, [payload.message!]));
          } else if (
            acceptStreamUpdate &&
            !payload.message &&
            (payload.event.type === "text_delta" || payload.event.type === "thinking_delta")
          ) {
            setMessages((current) => applyHermesStreamDelta(current, payload.event));
          }

          const toolStatus = statusFromStreamEvent(payload.event);
          if (forActiveChat) {
            if (toolStatus) {
              setStreamStatus(toolStatus);
            } else if (
              payload.event.type === "text_delta" ||
              payload.event.type === "context_compacted" ||
              (payload.event.type === "karpathy_wiki" && payload.event.status === "done") ||
              (payload.event.type === "brand_site_authoring" &&
                (payload.event.status === "done" || payload.event.status === "error"))
            ) {
              // 正文开始写出后再清工具文案。thinking_delta 清掉会导致「工具执行中」刚出现又消失。
              setStreamStatus(null);
            }
            if (
              payload.event.type === "tool_result" &&
              isBrandSiteDraftMutatingToolName(payload.event.delta)
            ) {
              setBrandSiteDraftMutationHint(null);
              setBrandSiteDraftRevision((n) => n + 1);
            }
          }

          if (!forActiveChat) return;

          if (payload.event.type === "message_deleted") {
            const deletedId = payload.event.message_id;
            if (typeof deletedId === "number") {
              setMessages((current) => removeHermesMessage(current, deletedId));
            }
            return;
          }

          if (payload.event.type === "cancelled") {
            const cancelledUserId = payload.event.user_message_id;
            if (typeof cancelledUserId === "number") {
              setMessages((current) => removeHermesTurn(current, cancelledUserId));
            }
            // 过期 cancelled：当前已在等另一轮，只删旧轮、不解锁
            const pendingId = pendingUserMessageIdRef.current;
            if (
              pendingId != null &&
              typeof cancelledUserId === "number" &&
              cancelledUserId !== pendingId
            ) {
              return;
            }
            ignoreStreamUntilTerminalRef.current = false;
            pendingUserMessageIdRef.current = null;
            setIsSending(false);
            setStreamStatus(null);
            return;
          }

          // Another window (or this one) started a turn — lock send until terminal.
          if (payload.event.type === "message_created") {
            if (payload.message?.id) {
              pendingUserMessageIdRef.current = payload.message.id;
            }
            if (payload.message?.role === "user" && payload.message.content?.trim()) {
              lastSubmittedPromptRef.current = payload.message.content;
            }
            ignoreStreamUntilTerminalRef.current = false;
            setIsSending(true);
            return;
          }

          if (payload.event.type === "retry_started") {
            const userMessageId =
              typeof payload.event.message_id === "number"
                ? payload.event.message_id
                : null;
            if (userMessageId) {
              pendingUserMessageIdRef.current = userMessageId;
            }
            ignoreStreamUntilTerminalRef.current = false;
            setIsSending(true);
            setStreamStatus(null);
            return;
          }

          if (!isHermesTerminalEvent(payload.event)) {
            // Locally stopped: keep ignoring mid-stream until terminal cancelled/done/error.
            if (ignoreStreamUntilTerminalRef.current) {
              return;
            }
            // Assistant deltas / tool_call from any window: show in-progress.
            if (!isSendingRef.current) {
              setIsSending(true);
            }
            return;
          }

          // done / error：若带有 user_message_id 且与当前待回复轮不一致，视为过期终态
          const terminalUserMessageId =
            typeof payload.event.user_message_id === "number"
              ? payload.event.user_message_id
              : null;
          const pendingId = pendingUserMessageIdRef.current;
          if (
            pendingId != null &&
            terminalUserMessageId != null &&
            terminalUserMessageId !== pendingId
          ) {
            return;
          }
          // Stop 后仍 ignore 时：旧轮 done/error 只清 ignore，不误伤（消息已乐观删除）
          if (ignoreStreamUntilTerminalRef.current && terminalUserMessageId != null) {
            ignoreStreamUntilTerminalRef.current = false;
            setIsSending(false);
            setStreamStatus(null);
            return;
          }

          ignoreStreamUntilTerminalRef.current = false;
          pendingUserMessageIdRef.current = null;
          setIsSending(false);
          setStreamStatus(null);

          if (!openRef.current) {
            if (payload.event.type === "error") {
              toast.error(payload.event.delta?.trim() || "这次执行失败。请稍后重试。");
            } else if (payload.event.type === "cancelled") {
              // Stop: no toast — user intentionally halted generation; turn was deleted.
            } else {
              toast.message("Hermes 已回复", {
                description: "打开助手查看完整内容",
                action: {
                  label: "打开",
                  onClick: () => setOpen(true),
                },
              });
            }
            if (HERMES_AUTO_REOPEN_ON_DONE && payload.event.type === "done") {
              setOpen(true);
            }
          }
        },
      },
    ) as HermesCableSubscription;

    subscriptionRef.current = subscription;

    return () => {
      subscriptionRef.current = null;
      subscription.unsubscribe();
    };
  }, [accountId, catchUpStream, cableReconnectGen]);

  // Safety net: when a tab regains focus, merge latest messages (Cable remains primary).
  useEffect(() => {
    const refreshIfVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;

      const activeChatId = chatIdRef.current;
      const activeAccountId = accountIdRef.current;
      if (!activeChatId || !activeAccountId) return;

      void (async () => {
        try {
          const hermes = createHermesApi(activeAccountId);
          const { messages: chatMessages } = await hermes.listMessages(activeChatId);
          setMessages((current) => mergeHermesMessages(current, chatMessages));
          const inFlight = hermesInFlightTurn(chatMessages);
          if (inFlight.sending) {
            pendingUserMessageIdRef.current = inFlight.userMessageId;
            setIsSending(true);
          }
          if (isSendingRef.current || inFlight.sending) {
            await catchUpStream(activeAccountId, activeChatId, pendingUserMessageIdRef.current ?? undefined);
          }
        } catch {
          // Best-effort; live Cable remains primary.
        }
      })();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") refreshIfVisible();
    };

    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [catchUpStream]);

  const sendMessage = useCallback(
    async (content: string) => {
      const trimmed = content.trim();
      if (!trimmed) return;
      if (isSendingRef.current) {
        throw new Error("Hermes is still generating");
      }

      if (!accountId) {
        toast.error("请先进入账户工作区后再使用 Hermes");
        throw new Error("Hermes requires an account workspace");
      }

      const hermes = createHermesApi(accountId);
      const clientId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `hermes-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setIsSending(true);
      setStreamStatus(null);
      lastSubmittedPromptRef.current = trimmed;
      pauseAutoDrainRef.current = false;
      // Stop 后保持 ignore，直到本轮 user 消息落库，避免旧流 delta 再次锁住发送态。

      try {
        // 知识录入等场景可能已有 chatId，但仍需等 in-flight listWikis 播种后再 snapshot/create/update。
        await waitForDraftSeed();
        await waitForDraftPersist();
        let activeChatId = chatIdRef.current;
        const currentDraft = snapshotDraft();
        const persistSkills = persistSkillsForDraft(currentDraft, draftSkillsRef.current);
        const uiSkills = hermesUiSkills(persistSkills);

        if (!activeChatId) {
          const bindingFields = capabilityContextFields(currentDraft);
          const chatContext = knowledgeEntry
            ? {
                ...bindingFields,
                ...knowledgeEntryChatContext(knowledgeEntry),
                kb_bindings: currentDraft.kbBindings,
              }
            : platformTemplateIdeRef.current
              ? platformTemplateIdeChatContext(platformTemplateIdeRef.current)
              : brandSiteAuthoringRef.current
                ? withBrandSiteAuthoringMode(
                    currentDraft,
                    brandSiteAuthoringChatContext(brandSiteAuthoringRef.current),
                  )
                : journeyAuthoringRef.current
                  ? withJourneyAuthoringMode(
                      currentDraft,
                      journeyAuthoringChatContext(journeyAuthoringRef.current),
                    )
                  : bindingFields;
          const { chat } = await hermes.createChat(persistSkills, chatContext);
          activeChatId = chat.id;
          chatIdRef.current = chat.id;
          setChatId(chat.id);
          setChatSkills(uiSkills);
        } else if (platformTemplateIdeRef.current) {
          await hermes.updateChat(activeChatId, {
            context: platformTemplateIdeChatContext(platformTemplateIdeRef.current),
          });
        } else if (brandSiteAuthoringRef.current) {
          await hermes.updateChat(activeChatId, {
            context: withBrandSiteAuthoringMode(
              currentDraft,
              brandSiteAuthoringChatContext(brandSiteAuthoringRef.current),
            ),
          });
        }

        // 每轮都传当前技能与绑定，让会话中途调整在下一次回复生效；空数组也会清除旧绑定。
        const { message: userMessage } = await hermes.sendMessage(
          activeChatId,
          trimmed,
          clientId,
          persistSkills,
          currentDraft.kbBindings,
          {
            materials_bindings: currentDraft.materials,
            journey_bindings: currentDraft.journey,
            brand_site_bindings: currentDraft.brandSite,
          },
        );
        ignoreStreamUntilTerminalRef.current = false;
        setChatSkills(uiSkills);
        setMessages((current) => mergeHermesMessages(current, [userMessage]));
        pendingUserMessageIdRef.current = userMessage.id;
        // Reply arrives via HermesStreamChannel — no client wait/poll timeout.
      } catch (error) {
        ignoreStreamUntilTerminalRef.current = false;
        pendingUserMessageIdRef.current = null;
        lastSubmittedPromptRef.current = null;
        setIsSending(false);
        setStreamStatus(null);
        const message = error instanceof ApiError ? error.message : "发送失败，请稍后重试";
        toast.error(message);
        throw error;
      }
    },
    [accountId, knowledgeEntry, snapshotDraft, waitForDraftPersist, waitForDraftSeed],
  );

  const enqueueMessage = useCallback((content: string) => {
    setQueuedMessages((current) => enqueueHermesMessage(current, content));
  }, []);

  const updateQueuedMessage = useCallback((id: string, content: string) => {
    setQueuedMessages((current) => updateHermesQueuedMessage(current, id, content));
  }, []);

  const removeQueuedMessage = useCallback((id: string): HermesQueuedMessage | null => {
    const removed = queuedMessagesRef.current.find((entry) => entry.id === id) ?? null;
    setQueuedMessages((current) => removeHermesQueuedMessage(current, id).queue);
    return removed;
  }, []);

  const sendOrEnqueue = useCallback(
    async (content: string): Promise<{ queued: boolean }> => {
      const trimmed = content.trim();
      if (!trimmed) return { queued: false };
      if (isSendingRef.current) {
        enqueueMessage(trimmed);
        return { queued: true };
      }
      await sendMessage(trimmed);
      return { queued: false };
    },
    [enqueueMessage, sendMessage],
  );

  const sendQueuedNow = useCallback(
    async (id: string) => {
      const item = queuedMessagesRef.current.find((entry) => entry.id === id);
      if (!item) return;
      setQueuedMessages((current) => removeHermesQueuedMessage(current, id).queue);
      pauseAutoDrainRef.current = true;
      if (isSendingRef.current) {
        stopGeneration();
      }
      pauseAutoDrainRef.current = false;
      await sendMessage(item.content);
    },
    [sendMessage, stopGeneration],
  );

  // 生成自然结束后自动发送队首；Stop 后暂停，直到用户再发或插队发送。
  useEffect(() => {
    if (isSending || isSwitchingSession) return;
    if (pauseAutoDrainRef.current) return;
    if (drainInFlightRef.current) return;
    const next = queuedMessages[0];
    if (!next) return;

    drainInFlightRef.current = true;
    setQueuedMessages((current) => current.slice(1));
    void sendMessage(next.content)
      .catch(() => {
        setQueuedMessages((current) => [next, ...current]);
      })
      .finally(() => {
        drainInFlightRef.current = false;
      });
  }, [isSending, isSwitchingSession, queuedMessages, sendMessage]);

  const retryMessage = useCallback(
    async (assistantMessageId: number) => {
      if (!assistantMessageId || isSendingRef.current) return;

      if (!accountId) {
        toast.error("请先进入账户工作区后再使用 Hermes");
        return;
      }

      const activeChatId = chatId;
      if (!activeChatId) return;

      const hermes = createHermesApi(accountId);
      setIsSending(true);
      setStreamStatus(null);
      ignoreStreamUntilTerminalRef.current = false;

      try {
        const { deleted_message_id, user_message_id } = await hermes.retryMessage(
          activeChatId,
          assistantMessageId,
        );
        setMessages((current) => removeHermesMessage(current, deleted_message_id));
        pendingUserMessageIdRef.current = user_message_id;
        const priorUser = messagesRef.current.find((message) => message.id === user_message_id);
        if (priorUser?.content?.trim()) {
          lastSubmittedPromptRef.current = priorUser.content;
        }
      } catch (error) {
        pendingUserMessageIdRef.current = null;
        setIsSending(false);
        setStreamStatus(null);
        const message = error instanceof ApiError ? error.message : "重试失败，请稍后重试";
        toast.error(message);
      }
    },
    [accountId, chatId],
  );

  const value = useMemo(
    () => ({
      open,
      setOpen,
      expanded,
      setExpanded,
      accountId,
      chatId,
      chatSkills,
      draftSkills,
      setDraftSkills,
      draftKbBindings,
      setDraftKbBindings,
      capabilityDraft,
      setCapabilityDraft,
      messages,
      isSending,
      streamStatus,
      sendOrEnqueue,
      sendMessage,
      queuedMessages,
      enqueueMessage,
      updateQueuedMessage,
      removeQueuedMessage,
      sendQueuedNow,
      retryMessage,
      stopGeneration,
      startNewSession,
      switchSession,
      composerFocusNonce,
      isSwitchingSession,
      knowledgeEntry,
      startKnowledgeEntrySession,
      updateKnowledgeEntryStructured,
      journeyAuthoring,
      lastJourneyAuthoring,
      startJourneyAuthoringSession,
      stopJourneyAuthoring,
      brandSiteAuthoring,
      lastBrandSiteAuthoring,
      prepareBrandSiteAuthoring,
      startBrandSiteAuthoringSession,
      stopBrandSiteAuthoring,
      brandSiteSessionReady,
      brandSiteStudioActive,
      startBrandSiteStudioSession,
      switchBrandSiteStudioSession,
      restartBrandSiteStudioSession,
      stopBrandSiteStudio,
      brandSiteScoreOptimizeActive,
      startBrandSiteScoreOptimizeSession,
      stopBrandSiteScoreOptimize,
      restartBrandSiteScoreOptimizeSession,
      composerSeed,
      clearComposerSeed,
      platformTemplateIde,
      lastPlatformTemplateIde,
      preparePlatformTemplateIde,
      startPlatformTemplateIdeSession,
      stopPlatformTemplateIde,
      sessionTitles,
      brandSiteDraftRevision,
      brandSiteDraftMutationHint,
      notifyBrandSiteDraftMutated,
      clearBrandSiteDraftMutationHint,
    }),
    [
      open,
      setOpen,
      expanded,
      accountId,
      chatId,
      chatSkills,
      draftSkills,
      setDraftSkills,
      draftKbBindings,
      setDraftKbBindings,
      capabilityDraft,
      setCapabilityDraft,
      messages,
      isSending,
      streamStatus,
      sendOrEnqueue,
      sendMessage,
      queuedMessages,
      enqueueMessage,
      updateQueuedMessage,
      removeQueuedMessage,
      sendQueuedNow,
      retryMessage,
      stopGeneration,
      startNewSession,
      switchSession,
      composerFocusNonce,
      isSwitchingSession,
      knowledgeEntry,
      startKnowledgeEntrySession,
      updateKnowledgeEntryStructured,
      journeyAuthoring,
      lastJourneyAuthoring,
      startJourneyAuthoringSession,
      stopJourneyAuthoring,
      brandSiteAuthoring,
      lastBrandSiteAuthoring,
      prepareBrandSiteAuthoring,
      startBrandSiteAuthoringSession,
      stopBrandSiteAuthoring,
      brandSiteSessionReady,
      brandSiteStudioActive,
      startBrandSiteStudioSession,
      switchBrandSiteStudioSession,
      restartBrandSiteStudioSession,
      stopBrandSiteStudio,
      brandSiteScoreOptimizeActive,
      startBrandSiteScoreOptimizeSession,
      stopBrandSiteScoreOptimize,
      restartBrandSiteScoreOptimizeSession,
      composerSeed,
      clearComposerSeed,
      platformTemplateIde,
      lastPlatformTemplateIde,
      preparePlatformTemplateIde,
      startPlatformTemplateIdeSession,
      stopPlatformTemplateIde,
      sessionTitles,
      brandSiteDraftRevision,
      brandSiteDraftMutationHint,
      notifyBrandSiteDraftMutated,
      clearBrandSiteDraftMutationHint,
    ],
  );

  return <HermesContext.Provider value={value}>{children}</HermesContext.Provider>;
}
